import type { Client, Sale, BuyerFrequency, RiskFlag } from '@/types';
import { getDaysAgo, getDaysUntil, predictNextPurchaseDate, isOverdue } from './dateUtils';
import { calculateAverage, clamp } from './numberUtils';

export const calculateClientMetrics = (sales: Sale[]): Partial<Client> => {
  if (sales.length === 0) {
    return {
      totalPurchases: 0,
      totalPurchaseKg: 0,
      totalPurchaseValue: 0,
      averageOrderSizeKg: 0,
      averageOrderValue: 0,
      averagePricePerKg: 0,
    };
  }

  // Sort sales by date
  const sortedSales = [...sales].sort((a, b) => 
    new Date(a.saleDate).getTime() - new Date(b.saleDate).getTime()
  );

  // Basic calculations
  const totalPurchases = sales.length;
  const totalPurchaseKg = sales.reduce((sum, s) => sum + s.quantityKg, 0);
  const totalPurchaseValue = sales.reduce((sum, s) => sum + s.totalValue, 0);
  const averageOrderSizeKg = totalPurchaseKg / totalPurchases;
  const averageOrderValue = totalPurchaseValue / totalPurchases;
  const averagePricePerKg = totalPurchaseValue / totalPurchaseKg;

  // Date calculations
  const lastPurchaseDate = new Date(sortedSales[sortedSales.length - 1].saleDate);
  const secondLastPurchaseDate = sortedSales.length > 1 
    ? new Date(sortedSales[sortedSales.length - 2].saleDate)
    : undefined;
  const firstPurchaseDate = new Date(sortedSales[0].saleDate);

  // Calculate days between purchases
  const daysBetweenPurchases: number[] = [];
  for (let i = 1; i < sortedSales.length; i++) {
    const days = Math.abs(
      new Date(sortedSales[i].saleDate).getTime() - 
      new Date(sortedSales[i - 1].saleDate).getTime()
    ) / (1000 * 60 * 60 * 24);
    daysBetweenPurchases.push(days);
  }

  const averageDaysBetweenPurchases = daysBetweenPurchases.length > 0
    ? calculateAverage(daysBetweenPurchases)
    : 0;

  const daysSinceLastPurchase = getDaysAgo(lastPurchaseDate);

  // Predict next purchase
  const predictedNextPurchase = averageDaysBetweenPurchases > 0
    ? predictNextPurchaseDate(lastPurchaseDate, averageDaysBetweenPurchases)
    : undefined;

  const daysUntilNextPurchase = predictedNextPurchase
    ? getDaysUntil(predictedNextPurchase)
    : undefined;

  const isClientOverdue = predictedNextPurchase 
    ? isOverdue(predictedNextPurchase)
    : false;

  // Determine frequency
  const purchaseFrequency = determinePurchaseFrequency(averageDaysBetweenPurchases, totalPurchases);

  // Determine risk flag
  const riskFlag = determineRiskFlag(
    daysSinceLastPurchase,
    totalPurchaseValue,
    purchaseFrequency,
    isClientOverdue
  );

  // Calculate buying probability
  const buyingProbabilityScore = calculateBuyingProbability(
    daysSinceLastPurchase,
    averageDaysBetweenPurchases,
    purchaseFrequency,
    isClientOverdue
  );

  // Generate call recommendation
  const callRecommendation = generateCallRecommendation(
    daysSinceLastPurchase,
    daysUntilNextPurchase,
    isClientOverdue,
    buyingProbabilityScore
  );

  const shouldCallToday = buyingProbabilityScore > 60 || isClientOverdue;

  // Find preferences (most common choices)
  const preferredOnionSize = findMostCommon(sales.map(s => s.onionSize));
  const preferredOnionType = findMostCommon(sales.map(s => s.onionType));
  const preferredPaymentMethod = findMostCommon(sales.map(s => s.paymentMethod));

  return {
    totalPurchases,
    totalPurchaseKg,
    totalPurchaseValue,
    averageOrderSizeKg,
    averageOrderValue,
    averagePricePerKg,
    purchaseFrequency,
    averageDaysBetweenPurchases,
    lastPurchaseDate,
    secondLastPurchaseDate,
    predictedNextPurchase,
    daysSinceLastPurchase,
    daysUntilNextPurchase,
    isOverdue: isClientOverdue,
    riskFlag,
    buyingProbabilityScore,
    callRecommendation,
    shouldCallToday,
    preferredOnionSize,
    preferredOnionType,
    preferredPaymentMethod,
    firstPurchaseDate,
    lastUpdated: new Date(),
  };
};

const determinePurchaseFrequency = (avgDays: number, totalPurchases: number): BuyerFrequency => {
  if (totalPurchases === 1) return 'New';
  if (avgDays <= 7) return 'Weekly';
  if (avgDays <= 14) return 'Bi-weekly';
  if (avgDays <= 31) return 'Monthly';
  return 'Irregular';
};

const determineRiskFlag = (
  daysSince: number,
  totalValue: number,
  frequency: BuyerFrequency,
  overdue: boolean
): RiskFlag => {
  if (totalValue > 100000 && frequency !== 'Irregular') return 'High-value';
  if (daysSince > 30 || (overdue && daysSince > 14)) return 'Inactive';
  if (overdue && daysSince > 7) return 'At-risk';
  return 'Regular';
};

const calculateBuyingProbability = (
  daysSince: number,
  avgDays: number,
  frequency: BuyerFrequency,
  overdue: boolean
): number => {
  let score = 50; // Base score

  // Adjust based on days since last purchase
  if (avgDays > 0) {
    const ratio = daysSince / avgDays;
    if (ratio >= 0.8 && ratio <= 1.2) {
      score += 30; // Close to expected purchase time
    } else if (ratio > 1.2) {
      score += 20; // Overdue, likely needs stock
    } else if (ratio < 0.5) {
      score -= 30; // Too soon
    }
  }

  // Adjust based on frequency
  if (frequency === 'Weekly') score += 15;
  else if (frequency === 'Bi-weekly') score += 10;
  else if (frequency === 'Monthly') score += 5;
  else if (frequency === 'Irregular') score -= 10;

  // Adjust if overdue
  if (overdue) score += 15;

  return clamp(score, 0, 100);
};

const generateCallRecommendation = (
  daysSince: number,
  daysUntil: number | undefined,
  overdue: boolean,
  probability: number
): string => {
  if (overdue) {
    return `Client overdue by ${daysSince} days - Call ASAP`;
  }
  if (probability > 80) {
    return 'High probability buyer - Call today';
  }
  if (daysUntil !== undefined && daysUntil <= 2 && daysUntil >= 0) {
    return `Call in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`;
  }
  if (probability > 60) {
    return 'Likely to buy soon - Schedule call';
  }
  if (daysSince > 30) {
    return 'Client inactive - Re-engagement needed';
  }
  return 'Monitor for next purchase cycle';
};

const findMostCommon = <T>(arr: T[]): T | undefined => {
  if (arr.length === 0) return undefined;
  const counts = arr.reduce((acc, val) => {
    acc[String(val)] = (acc[String(val)] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  const max = Math.max(...Object.values(counts));
  const mostCommon = Object.keys(counts).find(key => counts[key] === max);
  return mostCommon as T;
};

export const generateTalkingPoints = (client: Client, recentSales: Sale[]): string[] => {
  const points: string[] = [];

  if (recentSales.length > 0) {
    const lastSale = recentSales[0];
    points.push(`Last bought ${lastSale.quantityKg}kg at ${lastSale.pricePerKg}/kg on ${new Date(lastSale.saleDate).toLocaleDateString()}`);
  }

  if (client.averageDaysBetweenPurchases > 0) {
    points.push(`Usually buys every ${Math.round(client.averageDaysBetweenPurchases)} days`);
  }

  if (client.preferredOnionSize) {
    points.push(`Prefers ${client.preferredOnionSize} size, ${client.preferredOnionType} type`);
  }

  if (client.averageOrderSizeKg > 0) {
    points.push(`Average order: ${Math.round(client.averageOrderSizeKg)}kg`);
  }

  if (client.isOverdue) {
    points.push(`⚠️ Overdue - likely needs restocking`);
  }

  return points;
};
